// Miky Motorsport - ghost: nahravani/prehravani trajektorie (M5)
// Ghost = vizualni prehrani zaznamu (zadny physics-replay -> determinismus neni potreba).
// Format je kompaktni binarni (quantizovany int16) kvuli ulozeni do KV / localStorage.
//
// Frame: cas (implicitni z indexu × dt) + pozice (x,y,z) + rotace (quaternion x,y,z,w).
// Quantizace: pozice v cm (int16, rozsah ±327 m), quat slozky ×32767 (int16).

import { catmull } from './trackMath.js';

const MAGIC = 0x4d47; // 'MG'
const VERSION = 1;
const HEADER_BYTES = 24; // magic,ver (2×u16) + trackHash u32 + frameCount u32 + dtMs f32 + lapMs f32
const FRAME_BYTES = 14; // 7 × int16 (pos xyz + quat xyzw)
const POS_SCALE = 100; // cm
const Q_SCALE = 32767;

// jednoduchy 32-bit hash retezce (track id / verze) - oznaceni nekompatibilnich ghostu
export function hashStr(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

function clampI16(v) {
  v = Math.round(v);
  return v < -32768 ? -32768 : v > 32767 ? 32767 : v;
}

// Recorder: sbira vzorky behem zavodu (volat per tick s pozici a kvaternionem).
export class GhostRecorder {
  constructor(dtMs = 1000 / 30) {
    this.dtMs = dtMs;
    this.frames = []; // {x,y,z,qx,qy,qz,qw}
    this._acc = 0;
    this._first = true;
  }

  // dt v sekundach; pos {x,y,z}; quat {x,y,z,w}
  sample(dt, pos, quat) {
    this._acc += dt * 1000;
    if (!this._first && this._acc < this.dtMs) return;
    this._acc = 0;
    this._first = false;
    this.frames.push({
      x: pos.x, y: pos.y, z: pos.z,
      qx: quat.x, qy: quat.y, qz: quat.z, qw: quat.w,
    });
  }

  get count() { return this.frames.length; }
}

// Zakoduje frames do ArrayBufferu.
export function encodeGhost(frames, { trackHash = 0, lapMs = 0, dtMs = 1000 / 30 } = {}) {
  const n = frames.length;
  const buf = new ArrayBuffer(HEADER_BYTES + n * FRAME_BYTES);
  const dv = new DataView(buf);
  dv.setUint16(0, MAGIC, true);
  dv.setUint16(2, VERSION, true);
  dv.setUint32(4, trackHash >>> 0, true);
  dv.setUint32(8, n, true);
  dv.setFloat32(12, dtMs, true);
  dv.setFloat32(16, lapMs, true);
  // 20..24 rezerva
  let o = HEADER_BYTES;
  for (let i = 0; i < n; i++) {
    const f = frames[i];
    dv.setInt16(o, clampI16(f.x * POS_SCALE), true); o += 2;
    dv.setInt16(o, clampI16(f.y * POS_SCALE), true); o += 2;
    dv.setInt16(o, clampI16(f.z * POS_SCALE), true); o += 2;
    dv.setInt16(o, clampI16(f.qx * Q_SCALE), true); o += 2;
    dv.setInt16(o, clampI16(f.qy * Q_SCALE), true); o += 2;
    dv.setInt16(o, clampI16(f.qz * Q_SCALE), true); o += 2;
    dv.setInt16(o, clampI16(f.qw * Q_SCALE), true); o += 2;
  }
  return buf;
}

// Dekoduje ArrayBuffer zpet na {meta, frames}.
export function decodeGhost(buf) {
  const dv = new DataView(buf);
  if (dv.getUint16(0, true) !== MAGIC) throw new Error('Ghost: spatny magic');
  const version = dv.getUint16(2, true);
  if (version !== VERSION) throw new Error('Ghost: nekompatibilni verze');
  const trackHash = dv.getUint32(4, true);
  const n = dv.getUint32(8, true);
  // overit delku pred alokaci (anti corrupted/crafted data -> RangeError / nadmerna alokace)
  if (buf.byteLength !== HEADER_BYTES + n * FRAME_BYTES) throw new Error('Ghost: nesouhlasi delka');
  const dtMs = dv.getFloat32(12, true);
  const lapMs = dv.getFloat32(16, true);
  const frames = new Array(n);
  let o = HEADER_BYTES;
  for (let i = 0; i < n; i++) {
    frames[i] = {
      x: dv.getInt16(o, true) / POS_SCALE,
      y: dv.getInt16(o + 2, true) / POS_SCALE,
      z: dv.getInt16(o + 4, true) / POS_SCALE,
      qx: dv.getInt16(o + 6, true) / Q_SCALE,
      qy: dv.getInt16(o + 8, true) / Q_SCALE,
      qz: dv.getInt16(o + 10, true) / Q_SCALE,
      qw: dv.getInt16(o + 12, true) / Q_SCALE,
    };
    o += FRAME_BYTES;
  }
  return { meta: { version, trackHash, dtMs, lapMs, count: n }, frames };
}

// base64 (de)serializace pro localStorage / JSON transport
export function ghostToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return (typeof btoa !== 'undefined') ? btoa(s) : Buffer.from(s, 'binary').toString('base64');
}

export function base64ToGhost(b64) {
  const s = (typeof atob !== 'undefined') ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
  return bytes.buffer;
}

// Player: prehravani s catmull-rom interpolaci pozice + slerp rotace.
export class GhostPlayer {
  constructor(frames, dtMs = 1000 / 30) {
    this.frames = frames;
    this.dtMs = dtMs;
    this.duration = frames.length > 0 ? (frames.length - 1) * dtMs : 0;
  }

  get valid() { return this.frames && this.frames.length >= 2; }

  // tMs: cas od startu zavodu (ms). Vrati {pos:{x,y,z}, quat:{x,y,z,w}} nebo null.
  sampleAt(tMs) {
    const f = this.frames;
    if (!f || f.length === 0) return null;
    if (f.length === 1) return frameToOut(f[0]);
    const fi = tMs / this.dtMs;
    const i = Math.floor(fi);
    if (i < 0) return frameToOut(f[0]);
    if (i >= f.length - 1) return frameToOut(f[f.length - 1]);
    const t = fi - i;
    const p0 = f[Math.max(0, i - 1)];
    const p1 = f[i];
    const p2 = f[i + 1];
    const p3 = f[Math.min(f.length - 1, i + 2)];
    return {
      pos: {
        x: catmull(p0.x, p1.x, p2.x, p3.x, t),
        y: catmull(p0.y, p1.y, p2.y, p3.y, t),
        z: catmull(p0.z, p1.z, p2.z, p3.z, t),
      },
      quat: slerpQuat(p1, p2, t),
    };
  }
}

function frameToOut(f) {
  return { pos: { x: f.x, y: f.y, z: f.z }, quat: { x: f.qx, y: f.qy, z: f.qz, w: f.qw } };
}

// Slerp dvou quaternionu (z frame struktury).
function slerpQuat(a, b, t) {
  let ax = a.qx, ay = a.qy, az = a.qz, aw = a.qw;
  let bx = b.qx, by = b.qy, bz = b.qz, bw = b.qw;
  let dot = ax * bx + ay * by + az * bz + aw * bw;
  if (dot < 0) { bx = -bx; by = -by; bz = -bz; bw = -bw; dot = -dot; }
  if (dot > 0.9995) {
    // line, normalizovat
    const x = ax + (bx - ax) * t, y = ay + (by - ay) * t, z = az + (bz - az) * t, w = aw + (bw - aw) * t;
    const l = Math.hypot(x, y, z, w) || 1;
    return { x: x / l, y: y / l, z: z / l, w: w / l };
  }
  const theta0 = Math.acos(dot);
  const theta = theta0 * t;
  const s0 = Math.cos(theta) - dot * Math.sin(theta) / Math.sin(theta0);
  const s1 = Math.sin(theta) / Math.sin(theta0);
  return {
    x: ax * s0 + bx * s1,
    y: ay * s0 + by * s1,
    z: az * s0 + bz * s1,
    w: aw * s0 + bw * s1,
  };
}
